'use client';

import { useEffect, useState } from 'react';

import { concertLine, peakTimesLine, type UrgencyAudience } from '@/config/urgency';

interface TrialUrgencyProps {
  audience?: UrgencyAudience;
  /** `dark` for sections on a dark background or a video overlay. */
  tone?: 'light' | 'dark';
  /** `subheadline` continues a hero's subheadline in its own type, as prose. */
  variant?: 'line' | 'subheadline';
  className?: string;
}

const TONE_CLASSES = {
  light: 'text-gray-600',
  dark: 'text-gray-100 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]',
} as const;

/**
 * One true line above a trial CTA. The peak-times line renders on the server;
 * the concert line needs a real clock, so it swaps in after mount and a
 * statically built page can never show a deadline that has already passed.
 */
export function TrialUrgency({
  audience = 'everyone',
  tone = 'light',
  variant = 'line',
  className = '',
}: TrialUrgencyProps) {
  const [line, setLine] = useState(() => peakTimesLine(audience));

  useEffect(() => {
    setLine(concertLine() ?? peakTimesLine(audience));
  }, [audience]);

  if (variant === 'subheadline') {
    return (
      <p className={`text-base leading-7 text-gray-600 text-pretty sm:text-lg sm:leading-8 ${className}`}>{line}</p>
    );
  }

  return (
    <p className={`py-1 text-center text-sm font-medium text-pretty ${TONE_CLASSES[tone]} ${className}`}>
      {/* one sentence per line: the second sentence would otherwise wrap mid-phrase */}
      {sentences(line).map((sentence) => (
        <span key={sentence} className="block">
          {sentence}
        </span>
      ))}
    </p>
  );
}

const sentences = (text: string): string[] => text.split(/(?<=\.)\s+/);

export default TrialUrgency;
